/**
 * Landing closing sections, in page order:
 *   FinOsSection   — "Your Financial Operating System" profile cards + banner
 *   WhySection     — "WHY FINMINOS" fanned feature cards (forest band)
 *   TeamSection    — "MEET THE TEAM" mission + logos + quote card
 *   FinalCtaSection— "October 31 is closer than you think." (forest band)
 */
import * as React from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowRight } from 'lucide-react';
import { FIN_PROFILES, TEAM_LOGOS, WHY_CARDS } from './data';
import { CardMarquee, CtaLink, Kicker, LogoMarquee, SerifHeading } from './shared';

// ── Your Financial Operating System ──────────────────────────────────────────

export function FinOsSection() {
  return (
    <section className="bg-parchment">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24">
        <div className="text-center">
          <Kicker>Your financial operating system</Kicker>
          <SerifHeading className="mt-4" line1="Your Financial" line2="Operating System" />
          <p className="mx-auto mt-5 max-w-3xl text-sm leading-relaxed text-sage sm:text-base">
            Managing your finances shouldn&apos;t mean juggling multiple apps, advisors,
            spreadsheets, and deadlines. FinminOS brings everything together in one secure
            platform—helping you understand your finances, complete financial services, and
            make better decisions throughout the year.
          </p>
        </div>

        <CardMarquee className="mt-12">
          {FIN_PROFILES.map(({ icon: Icon, title, chips }) => (
            <div key={title} className="w-[330px] shrink-0 rounded-2xl bg-forest p-6">
              <Icon className="h-6 w-6 text-cream" aria-hidden />
              <p className="mt-3 text-lg font-bold text-cream">{title}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {chips.map((chip) => (
                  <span
                    key={chip}
                    className="rounded-full bg-cream/10 px-3 py-1 text-xs text-cream/85"
                  >
                    {chip}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </CardMarquee>

        {/* Butter CTA banner */}
        <div className="mt-12 flex flex-col items-center justify-between gap-6 rounded-2xl border border-amber/20 bg-butter/50 px-7 py-8 sm:px-10 lg:flex-row">
          <p className="font-landing text-xl font-semibold italic leading-snug text-forest sm:text-2xl">
            Technology when speed matters.{' '}
            <em className="text-amber">Experts when judgment matters.</em>
          </p>
          <CtaLink size="lg" className="shrink-0" />
        </div>
      </div>
    </section>
  );
}

// ── Why FinminOS: fanned cards ────────────────────────────────────────────────

/** rotation/offset per card, mirroring the design's fanned hand of cards */
const FAN_STYLES = [
  'lg:-rotate-[14deg] lg:translate-y-16',
  'lg:-rotate-[7deg] lg:translate-y-6',
  '',
  'lg:rotate-[7deg] lg:translate-y-6',
  'lg:rotate-[14deg] lg:translate-y-16',
];

export function WhySection() {
  // Mobile Figma shows the rail scrolled so the AI-Powered card (index 2) is
  // centered — reproduce that as the initial scroll position on small screens.
  const railRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const el = railRef.current;
    if (!el || window.innerWidth >= 1024) return;
    const center = el.children[2] as HTMLElement | undefined;
    if (center) el.scrollLeft = center.offsetLeft - (el.clientWidth - center.offsetWidth) / 2;
  }, []);

  return (
    <section className="relative overflow-hidden bg-forest-deep">
      <div
        className="pointer-events-none absolute -left-20 top-10 h-72 w-72 rounded-full border-[36px] border-cream/5"
        aria-hidden
      />

      <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24">
        <div className="text-center">
          <Kicker>Why FinminOS</Kicker>
          <SerifHeading
            dark
            className="mt-4"
            line1="Everything you need for a smarter,"
            line2="stress-free ITR filing"
          />
          <p className="mt-5 text-sm text-hero-muted-foreground sm:text-base">
            Most people file their taxes. Smart people optimise them. Here&apos;s what makes FinminOS different.
          </p>
          {/* Desktop: button sits between the copy and the fan (Figma desktop). */}
          <Link
            to="/login"
            className="mt-7 hidden items-center gap-2 rounded-[12px] border border-cream/10 bg-forest px-6 py-3.5 text-sm font-semibold text-cream transition-all hover:brightness-110 active:scale-95 lg:inline-flex"
          >
            Start Filing — It&apos;s Free
            <ArrowRight className="h-4 w-4" aria-hidden />
          </Link>
        </div>

        {/* Fanned cards — arch-topped photo cards. Desktop: fanned hand with
            hover-expansion. Mobile (per the mobile Figma): horizontal swipe
            rail of full-copy cards. */}
        <div
          ref={railRef}
          className="landing-rail -mx-4 mt-12 flex gap-3 overflow-x-auto px-4 lg:mx-0 lg:mt-20 lg:items-start lg:justify-center lg:gap-0 lg:-space-x-8 lg:overflow-visible lg:px-0 lg:pb-24"
        >
          {WHY_CARDS.map(({ icon: Icon, title, body, tone, bg }, i) => {
            const isCenter = i === 2;
            return (
              <div
                key={title}
                className={[
                  // Mobile (Figma): uniform 40px corners, roomy padding, ~420px tall.
                  // Desktop: arch-topped fan cards.
                  'group relative w-[327px] shrink-0 snap-center overflow-hidden rounded-[40px]',
                  'bg-cover bg-center px-8 py-12 shadow-xl transition-transform',
                  'lg:origin-bottom lg:rounded-t-[52px] lg:rounded-b-[24px] lg:p-6',
                  isCenter
                    ? 'z-10 min-h-[420px] lg:min-h-[390px] lg:w-[310px]'
                    : 'min-h-[420px] lg:min-h-[330px] lg:w-[260px] lg:hover:z-20 lg:hover:scale-105',
                  tone === 'amber' ? 'text-white' : 'text-forest',
                  FAN_STYLES[i] ?? '',
                ].join(' ')}
                style={{ backgroundImage: `url(${bg})` }}
              >
                {/* Mobile-only tone scrim — the stacked cards show their full
                    copy on small screens, so deepen the photo tint to keep the
                    text legible. Desktop keeps the exported artwork as-is. */}
                <div
                  className={[
                    'absolute inset-0 lg:hidden',
                    tone === 'amber' ? 'bg-amber/35' : 'bg-cream/50',
                  ].join(' ')}
                  aria-hidden
                />
                {/* Icon chip is a desktop-only detail (absent in the mobile Figma) */}
                <span
                  className={[
                    'relative hidden h-9 w-9 items-center justify-center rounded-full lg:flex',
                    tone === 'amber' ? 'bg-white/15' : 'bg-forest/10',
                  ].join(' ')}
                  aria-hidden
                >
                  <Icon className="h-[18px] w-[18px]" />
                </span>
                <p
                  className={[
                    'relative mt-2 text-center font-landing-body text-[22px] font-bold leading-snug lg:text-xl',
                    isCenter ? 'lg:underline lg:underline-offset-4' : 'lg:group-hover:underline lg:group-hover:underline-offset-4',
                  ].join(' ')}
                >
                  {title}
                </p>
                <p
                  className={[
                    'relative mt-5 text-center text-base font-medium leading-[1.65] lg:mt-4 lg:text-sm lg:leading-relaxed',
                    isCenter
                      ? ''
                      : 'lg:max-h-0 lg:opacity-0 lg:transition-all lg:duration-300 lg:group-hover:max-h-72 lg:group-hover:opacity-100',
                  ].join(' ')}
                >
                  {body}
                </p>
              </div>
            );
          })}
        </div>

        {/* Mobile: the button follows the cards (Figma mobile ordering). */}
        <div className="mt-10 text-center lg:hidden">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 rounded-[12px] border border-cream/10 bg-forest px-6 py-3.5 text-sm font-semibold text-cream transition-all hover:brightness-110 active:scale-95"
          >
            Start Filing — It&apos;s Free
            <ArrowRight className="h-4 w-4" aria-hidden />
          </Link>
        </div>
      </div>
    </section>
  );
}

// ── Meet the team ─────────────────────────────────────────────────────────────

export function TeamSection() {
  return (
    <section className="bg-white">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24">
        <div className="text-center">
          <Kicker>Meet the team</Kicker>
          <SerifHeading
            className="mt-4"
            line1="Built by people who wanted a better"
            line2="financial holistic system themselves"
          />
          <p className="mx-auto mt-5 max-w-3xl text-sm leading-relaxed text-sage sm:text-base">
            Our Mission: To make expert financial guidance accessible, transparent, structured
            and available throughout the year — at the right price.
          </p>
        </div>

        <div className="mt-10">
          <LogoMarquee logos={TEAM_LOGOS} fade="white" />
        </div>

        <div className="mt-10 rounded-2xl bg-forest px-7 py-10 text-center sm:px-10">
          <p className="mx-auto max-w-2xl text-base leading-relaxed text-cream sm:text-lg">
            Behind every recommendation is a team that believes financial decisions
            deserve clarity, not complexity.
          </p>
        </div>
      </div>
    </section>
  );
}

// ── Final CTA ─────────────────────────────────────────────────────────────────

export function FinalCtaSection() {
  return (
    <section className="relative overflow-hidden bg-forest-deep">
      <div
        className="pointer-events-none absolute left-1/2 top-1/2 h-[480px] w-[480px] -translate-x-1/2 -translate-y-1/2 rounded-full border-[60px] border-cream/5"
        aria-hidden
      />

      <div className="relative mx-auto max-w-4xl px-4 py-20 text-center sm:px-6 sm:py-28">
        <Kicker>Begin your journey</Kicker>
        <SerifHeading
          dark
          className="mt-4"
          line1="October 31 is closer than you think."
          line2="File your ITR today"
        />
        <p className="mx-auto mt-6 max-w-2xl text-sm leading-relaxed text-hero-muted-foreground sm:text-base">
          ₹5,000 penalty for missing the deadline. Up to ₹5 lakh in savings if you don&apos;t.
          The math is easy. Get started — it&apos;s free, and takes less than 2 minutes to see
          what you&apos;re owed.
        </p>

        <p className="mt-8 font-landing text-lg font-bold text-cream sm:text-xl">
          Technology when speed matters.
          <br />
          <em className="italic text-amber">Experts when judgment matters.</em>
        </p>

        <div className="mt-8">
          <CtaLink size="lg" />
        </div>

        <p className="mt-6 text-xs text-hero-muted-foreground/80">
          Free to start · No payment until you see your draft · Expert CA files your ITR in
          24 hours · ERI-2 Authorised
        </p>
      </div>
    </section>
  );
}
